import { faList, faLocationDot, faUnlockKeyhole } from '@fortawesome/pro-regular-svg-icons';
import { faAngleLeft, faPrint } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { PointsSummaryModal } from '@pickleballinc/ui/components/team-leagues/PointsSummaryModal';
import { Button } from '@pickleballinc/ui/components/ui/button';
import clsx from 'clsx';
import { useParams, useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';

import { MatchupStatusBadge } from '@/app/team-leagues/[teamLeagueId]/matchup/_components/MatchupStatusBadge';
import { UnlockMatchupModal } from '@/app/team-leagues/[teamLeagueId]/matchup/_components/UnlockMatchupModal';
import { DreamBreakerBox } from '@/components/molecules';
import { MlpScoreSheetPrint, OfficialScoreSheetPrint, PrintMatchesModal, SimplifiedScoreSheetPrint } from '@/components/molecules/prints';
import { CompleteMatchesModal } from '@/components/molecules/ScoreCards/modals';
import { EditMatchupStatusModal } from '@/components/organisms/ScoreCards/EditMatchupStatusModal';
import { Matchup } from '@/entities';
import {
	getMatchupStatusApiValue,
	MatchCompletedEnum,
	MatchStatusApiMap,
	MatchStatusEnum,
	MatchupCompletedEnum,
	MatchupStatusEnum
} from '@/entities/matchup/constants';
import { PermissionRole } from '@/entities/PermissionRoles';
import { CaptainLineupsAndScoresPermissions } from '@/entities/season-division/SeasonDivisionPool';
import { SystemLocationsCourts } from '@/entities/SystemLocationsCourts';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useSeasonDivisionSettings } from '@/hooks/leagueSeasonDivision/useSeasonDivisionSettings';
import { useUpdateMatchup } from '@/hooks/matches/useUpdateMatchup';
import { useTieBreakerMatch } from '@/hooks/tie-breaker/useTieBreakerMatch';
import { useRotationsInfoQuery } from '@/hooks/tie-breaker/useTieBreakerRotationsInfo';
import { publicEnv } from '@/lib/env';
import { useOrganizationStore } from '@/store/organization-store';
import { usePermissionStore } from '@/store/permission-store';
import { useTiebreakerStore } from '@/store/tie-breaker-store';
import { DateUtil } from '@/utils/date';
import { determineAreAllPlayersSelected, determinePlayerNameToDisplayOnPrint, formatTeamLeagueLabels } from '@/utils/helpers';
import { PodsResponse } from '@/utils/types';

interface MatchupHeaderProps {
	matchup: Matchup;
	canEditLineups: boolean;
	previousPageTitle: string;
	isTieBreaker?: boolean;
	isLineupPage?: boolean;
	podsResponse?: PodsResponse;
	systemLocationsCourts?: SystemLocationsCourts[];
}

interface FlagProps {
	isWinner: boolean;
	result: number;
	teamPoints: number;
}

export const Flag = ({ isWinner, result, teamPoints }: FlagProps) => {
	return (
		<div
			className={clsx('h-28 w-[90px] bg-orange-500 flex flex-col items-center justify-center text-white', {
				'bg-[url(/assets/images/winner-background.svg)]': isWinner
			})}
			style={{
				clipPath: 'polygon(0 0, 100% 0, 100% 100%, 50% 90%, 0 100%)'
			}}
		>
			<span className="text-4xl font-bold">{result}</span>
			<span className="text-center text-xs font-bold text-wrap">
				Team Points: <br /> {teamPoints}
			</span>
		</div>
	);
};

const TeamInfo = ({
	title,
	ranking,
	side,
	matchupStatus,
	pointsDiffPct,
	className
}: {
	title: string;
	ranking: string | number | null;
	side: 'Home' | 'Away';
	matchupStatus: MatchupStatusEnum;
	pointsDiffPct: number;
	className?: string;
}) => {
	const isAway = side === 'Away';
	return (
		<div className={className ?? ''}>
			<p
				className={`font-bold text-orange-500 text-sm md:text-2xl line-clamp-2 min-h-[2.5rem] md:min-h-[3.5rem] ${isAway ? 'text-end' : 'text-start'}`}
			>
				{title}
			</p>
			{isAway ? (
				<div className={`flex w-full ${ranking ? 'justify-between' : 'justify-end'}`}>
					{ranking ? <p className="text-gray-500 text-sm font-medium">#{ranking}</p> : null}
					<p className="text-gray-500 text-sm font-medium">{side}</p>
				</div>
			) : (
				<div className="flex w-full justify-between">
					<p className="text-gray-500 text-sm font-medium">{side}</p>
					{ranking ? <p className="text-gray-500 text-sm font-medium">#{ranking}</p> : null}
				</div>
			)}
			{matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS || matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ? (
				<p className={`text-gray-500 text-sm font-medium ${isAway ? 'place-self-end' : ''}`}>PD%: {pointsDiffPct}</p>
			) : null}
			{/* <p>{matchup.teamTwoRating}</p> */} {/* Team Rating Missing */}
		</div>
	);
};

export const FlagsBanner = ({ matchup }: { matchup: Matchup }) => {
	return (
		<div className="w-full border-t-2 border-orange-500 flex flex-col items-center sm:flex-row sm:items-start sm:justify-center gap-6 xl:justify-between xl:items-center">
			{/* Away Team */}
			<div className="hidden sm:flex sm:pt-2 xl:pt-0 flex-col flex-1 items-end xl:items-start">
				<img
					src={
						matchup.teamTwoLogo
							? getCDNURL(matchup.teamTwoLogo.url, 260, 160)
							: `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/placeholder/pickleball_transparent_square.png`
					}
					className="w-24 h-12 md:w-40 md:h-20 rounded-md bg-white"
					alt={matchup.teamTwoTitle}
				/>
				<TeamInfo
					title={`${matchup.teamTwoTitle ?? 'TBD'}`}
					ranking={matchup.teamTwoRanking}
					side="Away"
					matchupStatus={matchup.matchupStatus}
					pointsDiffPct={matchup.teamTwoPointsDiffPct}
					className="block xl:hidden"
				/>
			</div>

			{/* Center Part */}
			<div className="flex xl:items-center gap-6 flex-0">
				<div className="w-full">
					<TeamInfo
						title={matchup.teamTwoTitle ?? 'TBD'}
						ranking={matchup.teamTwoRanking}
						side="Away"
						matchupStatus={matchup.matchupStatus}
						pointsDiffPct={matchup.teamTwoPointsDiffPct}
						className="hidden xl:block"
					/>
				</div>
				<div className="flex xl:w-72">
					<Flag isWinner={matchup.winner === 2} result={matchup.teamTwoScore} teamPoints={matchup.teamTwoTeamPoints || 0} />
					<div className="h-28 w-16 bg-gray-900 flex items-center justify-center text-white text-sm">VS</div>
					<Flag isWinner={matchup.winner === 1} result={matchup.teamOneScore} teamPoints={matchup.teamOneTeamPoints || 0} />
				</div>
				<div className="w-full">
					<TeamInfo
						title={matchup.teamOneTitle ?? 'TBD'}
						ranking={matchup.teamOneRanking}
						side="Home"
						matchupStatus={matchup.matchupStatus}
						pointsDiffPct={matchup.teamOnePointsDiffPct}
						className="hidden xl:block"
					/>
				</div>
			</div>

			{/* Home Team */}
			<div className="hidden sm:flex sm:pt-2 xl:pt-0 flex-col flex-1 items-start xl:items-end">
				<img
					src={
						matchup.teamOneLogo
							? getCDNURL(matchup.teamOneLogo.url, 260, 160)
							: `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/placeholder/pickleball_transparent_square.png`
					}
					className="w-24 h-12 md:w-40 md:h-20 rounded-md bg-white"
					alt={matchup.teamTwoTitle}
				/>
				<TeamInfo
					title={matchup.teamOneTitle ?? 'TBD'}
					ranking={matchup.teamOneRanking}
					side="Home"
					matchupStatus={matchup.matchupStatus}
					pointsDiffPct={matchup.teamOnePointsDiffPct}
					className="block xl:hidden"
				/>
			</div>

			{/* mobile only */}
			<div className="sm:hidden flex gap-6">
				<div className="flex flex-col">
					<img
						src={
							matchup.teamTwoLogo
								? getCDNURL(matchup.teamTwoLogo.url, 260, 160)
								: `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/placeholder/pickleball_transparent_square.png`
						}
						className="w-24 h-12 md:w-40 md:h-20 rounded-md bg-white"
						alt={matchup.teamTwoTitle}
					/>
					<TeamInfo
						title={`${matchup.teamTwoTitle ?? 'TBD'}`}
						ranking={matchup.teamTwoRanking}
						side="Away"
						matchupStatus={matchup.matchupStatus}
						pointsDiffPct={matchup.teamTwoPointsDiffPct}
						className="self-end"
					/>
				</div>
				<div className="flex flex-col">
					<img
						src={
							matchup.teamOneLogo
								? getCDNURL(matchup.teamOneLogo.url, 260, 160)
								: `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/placeholder/pickleball_transparent_square.png`
						}
						className="w-24 h-12 md:w-40 md:h-20 rounded-md bg-white"
						alt={matchup.teamTwoTitle}
					/>
					<TeamInfo
						title={matchup.teamOneTitle ?? 'TBD'}
						ranking={matchup.teamOneRanking}
						side="Home"
						matchupStatus={matchup.matchupStatus}
						pointsDiffPct={matchup.teamOnePointsDiffPct}
						className="self-start"
					/>
				</div>
			</div>
		</div>
	);
};

export const MatchupHeader = ({
	matchup,
	isTieBreaker = false,
	canEditLineups,
	podsResponse,
	previousPageTitle,
	isLineupPage,
	systemLocationsCourts
}: MatchupHeaderProps) => {
	const params = useParams<{
		teamLeagueId: string;
		matchupId: string;
	}>();
	const router = useRouter();
	const permissionStore = usePermissionStore();
	const orgStore = useOrganizationStore();
	const teamLeagueInfo = useTeamLeagueInfo();
	const labels = formatTeamLeagueLabels(teamLeagueInfo);

	const scrollToTiebreaker = useTiebreakerStore((state) => state.scrollToTiebreaker);
	const { ianaTzIdentifier, abbreviation } = matchup.timezone;
	const matchupStartDate = DateUtil.formatUTCInTimezone(matchup.formattedStartDate, 'MMM D YYYY h:mm A', ianaTzIdentifier);

	const userTeamUuids = permissionStore.teamIds;

	const showPrintButton = !isTieBreaker;

	const { status: tieBreakerMatchStatus, match: tieBreakerMatch, tieBreakerTitle, matchCompletedType } = useTieBreakerMatch(matchup);
	const { data: rotationsInfo } = useRotationsInfoQuery(tieBreakerMatch?.matchUuid ?? '');
	const { data: poolData } = useSeasonDivisionSettings(matchup?.poolUuid || '');

	const isTeamTwoLineupSet = Array.isArray(rotationsInfo?.data?.teamTwo) && rotationsInfo.data.teamTwo.length > 0;
	const isTeamOneLineupSet = Array.isArray(rotationsInfo?.data?.teamOne) && rotationsInfo.data.teamOne.length > 0;
	const isTieBreakerLineupSet = isTeamOneLineupSet && isTeamTwoLineupSet;
	const isMatchupCompleted = matchup.matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS;
	const isTieBreakerActive = tieBreakerMatchStatus === MatchStatusApiMap[MatchStatusEnum.IN_PROGRESS_MATCH_STATUS];
	const isTieBreakerCompleted = tieBreakerMatchStatus === MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS];

	const isTieBreakerMatchCompleted =
		tieBreakerMatchStatus === MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS] &&
		matchCompletedType === MatchCompletedEnum.PLAYED_MATCH_COMPLETED_TYPE;

	const isHomeTeamCaptain = userTeamUuids.includes(matchup.teamLeagueTeamOneUuid);
	const isAwayTeamCaptain = userTeamUuids.includes(matchup.teamLeagueTeamTwoUuid);

	const isUserCaptainWithFullControlPermission =
		(isAwayTeamCaptain || isHomeTeamCaptain) &&
		poolData?.captainPermissions === CaptainLineupsAndScoresPermissions.FULL_CONTROL_CAPTAIN_PERMISSION;

	const isUserCaptainWithScoresPermission =
		(isAwayTeamCaptain || isHomeTeamCaptain) &&
		poolData?.captainPermissions === CaptainLineupsAndScoresPermissions.SCORES_ONLY_CAPTAIN_PERMISSION;

	const canCaptainCompleteMatchup = permissionStore.gamedayAccess || isUserCaptainWithFullControlPermission || isUserCaptainWithScoresPermission;

	const showCompleteMatchesButton = !isMatchupCompleted && canCaptainCompleteMatchup && !isTieBreakerActive;

	const isMatchupCancelledOrWithdrawn =
		(matchup.matchupCompletedType === MatchupCompletedEnum.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE ||
			matchup.matchupCompletedType === MatchupCompletedEnum.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE ||
			matchup.matchupCompletedType === MatchupCompletedEnum.CANCELED_MATCHUP_COMPLETION_TYPE) &&
		matchup.matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS;

	const showLineupsButton =
		!isMatchupCancelledOrWithdrawn && canEditLineups && !isTieBreakerActive && !isTieBreakerCompleted && !isMatchupCompleted;

	const shouldDisplayUnlockMatchupButton = permissionStore.gamedayAccess && matchup.matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS;
	const showSubstituteButton = !isMatchupCancelledOrWithdrawn && permissionStore.gamedayAccess && !shouldDisplayUnlockMatchupButton;

	const incompleteMatches = matchup.matches.filter(
		(match) => match.matchStatus !== MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS] && !match.isTieBreaker
	);

	const secondaryButtonClasses =
		'h-7 md:h-10 text-xs md:text-sm hover:bg-gray-50 bg-white border font-semibold text-sm rounded-md border-gray-300 text-center w-full';

	const [isEditMatchupModalOpen, setIsEditMatchupModalOpen] = useState(false);
	const [isPointsSummaryModalOpen, setIsPointsSummaryModalOpen] = useState(false);
	const [showPrintModal, setShowPrintModal] = useState(false);
	const [isCompleteMatchesModalOpen, setIsCompleteMatchesModalOpen] = useState<boolean>(false);
	const [isUnlockMatchupModalOpen, setIsUnlockMatchupModalOpen] = useState(false);
	const [selectedPrintOptions, setSelectedPrintOptions] = useState<{
		sheetType: 'official' | 'simplified' | 'mlp';
		sheetSize: 'half' | 'full';
	}>({
		sheetType: 'official',
		sheetSize: 'half'
	});

	const contentRef = useRef<HTMLDivElement>(null);

	const updateMatchupMutation = useUpdateMatchup();

	const handleUndoAction = () => {
		updateMatchupMutation.mutate(
			{
				matchup_uuid: matchup.uuid,
				matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.READY_TO_BE_STARTED_STATUS)
			},
			{
				onSuccess: () => {
					setIsEditMatchupModalOpen(false);
				}
			}
		);
	};

	const handlePrintModalOpen = () => {
		setShowPrintModal(true);
	};

	const handleCompleteMatchesClick = () => {
		setIsCompleteMatchesModalOpen(true);
	};

	const handleLineupsNavigation = () => {
		router.push(`/team-leagues/${params.teamLeagueId}/matchup/${params.matchupId}/lineups`);
	};

	const handleSubstituteNavigation = () => {
		router.push(`/team-leagues/${params.teamLeagueId}/matchup/${params.matchupId}/substitution`);
	};

	const generatePrintFilename = () => {
		const today = new Date();
		const dateStr = today.toLocaleDateString('en-CA').replace(/-/g, '');

		const eventTitle = matchup.seasonTitle?.replace(/[^a-zA-Z0-9]/g, '_') || 'TeamLeague';
		const sheetType = selectedPrintOptions.sheetType.charAt(0).toUpperCase() + selectedPrintOptions.sheetType.slice(1);
		const sheetSize = selectedPrintOptions.sheetSize === 'half' ? 'HalfSheet' : 'FullSheet';

		return `${eventTitle}_${sheetType}_${sheetSize}_${dateStr}.pdf`;
	};

	const reactToPrintFn = useReactToPrint({
		contentRef,
		documentTitle: generatePrintFilename()
	});

	const handlePrintWithOptions = (data: { sheetType: 'official' | 'simplified' | 'mlp'; sheetSize: 'half' | 'full' }) => {
		setSelectedPrintOptions(data);

		// Trigger print after a short delay to allow component to render
		setTimeout(() => {
			reactToPrintFn();
		}, 100);
	};

	const handleBackNavigation = () => {
		router.back();
	};

	const dreamBreakerProps = {
		courts: systemLocationsCourts ?? [],
		match: {
			matchUuid: tieBreakerMatch?.matchUuid ?? '',
			courtUuid: tieBreakerMatch?.courtUuid ?? '',
			matchAssignedToCourt: tieBreakerMatch?.matchAssignedToCourt ?? '',
			matchPlannedStart: tieBreakerMatch?.matchPlannedStart ?? ''
		},
		moduleUuid: matchup.matches[0]?.moduleUuid ?? '',
		locationUuid: matchup.locationUuid ?? ''
	};

	const shouldDisplayPlayerNames = determinePlayerNameToDisplayOnPrint(
		permissionStore.gamedayAccess,
		permissionStore.userPermission === PermissionRole.TEAM_CAPTAIN,
		permissionStore.teamIds.includes(matchup.teamLeagueTeamOneUuid) ? 'home' : 'away',
		determineAreAllPlayersSelected(true, false, matchup) && determineAreAllPlayersSelected(false, true, matchup),
		ianaTzIdentifier,
		matchup.lineupPublishedDate,
		orgStore.opponentLineupVisibility
	);

	const getBracketUrl = () => {
		const { poolUuid, inBracketType, teamLeagueTournamentUuid } = matchup;
		const teamLeagueId = orgStore.teamLeagueUuid;

		const bracketType = inBracketType === 'RR' ? 'pools' : 'elimination';

		return `${publicEnv.NEXT_PUBLIC_PTL_BRACKETS_URL}/team-leagues/${teamLeagueId}/tournaments/${teamLeagueTournamentUuid}/${bracketType}/${poolUuid}`;
	};

	return (
		<div>
			<div className="flex flex-col w-full">
				<div className="mb-2 sm:mb-6 flex h-6 items-center gap-2 print-hidden">
					<button
						onClick={handleBackNavigation}
						className="flex items-center gap-2 text-gray-900 hover:text-gray-700 hover:underline bg-transparent border-none cursor-pointer"
					>
						<FontAwesomeIcon icon={faAngleLeft} className="size-5" />
						<span className="text-base font-medium leading-6">{previousPageTitle}</span>
					</button>
				</div>
				<div className="flex justify-between items-center bg-[#FEFAF5] rounded-t-lg p-4 border border-orange-50 ">
					{/* Matchup info */}
					<div className="flex flex-col gap-2">
						<MatchupStatusBadge matchupStatus={matchup.matchupStatus} />
						<p className="font-semibold text-base text-gray-700">
							{matchupStartDate} {abbreviation}
						</p>
						{matchup.venue ? (
							<div className="flex items-center gap-2">
								<FontAwesomeIcon icon={faLocationDot} className="size-4 text-gray-500" />
								<p className="text-sm font-medium text-gray-500">{matchup.venue}</p>
							</div>
						) : null}
						{matchup.courts ? <p className="text-sm font-medium text-gray-500">{matchup.courts}</p> : null}
					</div>

					{/* Matchup buttons */}
					<div className="flex flex-col gap-2">
						{matchup.isTeamLeagueTournamentMatchup ? (
							<a href={getBracketUrl()}>
								<Button onClick={() => {}} className={secondaryButtonClasses} variant="secondary">
									View Bracket
								</Button>
							</a>
						) : null}
						{!!permissionStore.gamedayAccess && (
							<Button onClick={() => setIsEditMatchupModalOpen(true)} className={secondaryButtonClasses} variant="secondary">
								Edit Status
							</Button>
						)}
					</div>
				</div>
				<FlagsBanner matchup={matchup} />
				{matchup.isPointAllocationUsed ? (
					<div className="mt-3 flex justify-center">
						<Button className="rounded-lg font-semibold" onClick={() => setIsPointsSummaryModalOpen(true)} variant="outline">
							<FontAwesomeIcon icon={faList} className="mr-2" /> {formatText({ text: 'Points summary', role: TextRole.ButtonLabel })}
						</Button>
					</div>
				) : null}
				{/* Action Buttons */}
				{!isLineupPage && !isTieBreaker ? (
					<div className="w-full sm:w-auto self-end mt-4">
						<div className="flex flex-col gap-3">
							{showPrintButton && permissionStore.userPermission !== PermissionRole.NOT_AUTHORIZED ? (
								<Button className="rounded-lg font-semibold" onClick={handlePrintModalOpen} variant="outline">
									<FontAwesomeIcon icon={faPrint} className="mr-2" /> Print
								</Button>
							) : null}

							{!!showCompleteMatchesButton && <Button onClick={handleCompleteMatchesClick}>Complete matches</Button>}

							{showLineupsButton ? <Button onClick={handleLineupsNavigation}>Set lineups</Button> : null}

							{!!showSubstituteButton && (
								<Button onClick={handleSubstituteNavigation} className="font-semibold text-sm" variant="outline">
									Substitute
								</Button>
							)}
						</div>

						{shouldDisplayUnlockMatchupButton ? (
							<Button
								onClick={() => {
									setIsUnlockMatchupModalOpen(true);
								}}
								variant="secondary"
								className="hover:bg-gray-50 bg-white border font-semibold text-sm rounded-md py-2 border-gray-300 text-center w-full mt-3"
							>
								<FontAwesomeIcon icon={faUnlockKeyhole} className="me-2" />
								<p className="text-nowrap">Unlock {labels.matchupSingularTitle}</p>
							</Button>
						) : null}

						{isTieBreakerMatchCompleted ? (
							<Button onClick={scrollToTiebreaker} className="float-right mt-3 w-full">
								Go to {tieBreakerTitle}
							</Button>
						) : null}
					</div>
				) : (
					<div className="w-full sm:w-fit flex flex-col gap-3 self-end mt-4">
						<Button
							className="w-full sm:w-fit font-semibold text-sm bg-darkorange-400 hover:bg-orange-500 text-white rounded-md text-center whitespace-nowrap self-end"
							onClick={() => router.replace(`/team-leagues/${params.teamLeagueId}/matchup/${params.matchupId}`)}
						>
							Go to {labels.matchupSingularTitle}
						</Button>
						{!isLineupPage &&
							(matchup.matchupStatus === MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS ||
								matchup.matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ||
								matchup.matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS) && (
								<DreamBreakerBox {...dreamBreakerProps} matchupData={matchup} className="bg-white" />
							)}
					</div>
				)}
			</div>
			<PointsSummaryModal
				open={isPointsSummaryModalOpen}
				onClose={() => setIsPointsSummaryModalOpen(false)}
				teamOneTitle={matchup.teamOneTitle ?? 'TBD'}
				teamOnePointsSummary={matchup.teamOnePointsSummary}
				teamTwoTitle={matchup.teamTwoTitle ?? 'TBD'}
				teamTwoPointsSummary={matchup.teamTwoPointsSummary}
				matchupTitle={labels.matchupSingularTitle}
				tieBreakerTitle={labels.tieBreakerMatchTitle}
			/>

			<PrintMatchesModal open={showPrintModal} onOpenChange={setShowPrintModal} onPrint={handlePrintWithOptions} />

			<CompleteMatchesModal
				isOpen={isCompleteMatchesModalOpen}
				onClose={() => setIsCompleteMatchesModalOpen(false)}
				matchupId={matchup.uuid}
				incompleteMatches={incompleteMatches}
				tieBreakerMatchId={tieBreakerMatch?.matchUuid ?? ''}
				tieBreakerTitle={tieBreakerTitle}
				isTieBreakerLineupSet={isTieBreakerLineupSet}
				tieBreakerLink={`/team-leagues/${params.teamLeagueId}/matchup/${params.matchupId}/tie-breaker`}
				isTieBreakerCancelled={matchCompletedType === MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE}
			/>

			{isEditMatchupModalOpen ? (
				<EditMatchupStatusModal
					matchupStatus={matchup.matchupStatus}
					matchupCompletedType={matchup.matchupCompletedType}
					teamOneTitle={matchup.teamOneTitle ?? 'TBD'}
					teamTwoTitle={matchup.teamTwoTitle ?? 'TBD'}
					onClose={() => setIsEditMatchupModalOpen(false)}
					onConfirm={(status) => {
						updateMatchupMutation.mutate(
							{
								matchup_uuid: matchup.uuid,
								matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.COMPLETED_MATCHUP_STATUS),
								matchup_completed_type: status
							},
							{
								onSuccess: () => {
									setIsEditMatchupModalOpen(false);
								}
							}
						);
					}}
					onUndo={handleUndoAction}
					isSubmitting={updateMatchupMutation.isPending}
				/>
			) : null}

			{isUnlockMatchupModalOpen ? (
				<UnlockMatchupModal
					handleCancel={() => {
						setIsUnlockMatchupModalOpen(false);
					}}
					handleSubmit={() => {
						updateMatchupMutation.mutate(
							{
								matchup_uuid: matchup.uuid,
								matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS),
								matchup_completed_type: null
							},
							{
								onSuccess: () => {
									setIsUnlockMatchupModalOpen(false);
									toast.success('Matchup unlocked successfully.');
								}
							}
						);
					}}
					isSubmitDisabled={updateMatchupMutation.isPending}
				/>
			) : null}

			{selectedPrintOptions.sheetType === 'simplified' && (
				<SimplifiedScoreSheetPrint
					innerRef={contentRef}
					matches={matchup.matches}
					teamOneTitle={matchup.teamOneTitle ?? 'TBD'}
					teamTwoTitle={matchup.teamTwoTitle ?? 'TBD'}
					sheetSize={selectedPrintOptions.sheetSize === 'half' ? 'Half Sheets' : 'Full Sheets'}
					shouldDisplayPlayerNames={shouldDisplayPlayerNames}
				/>
			)}

			{selectedPrintOptions.sheetType === 'official' && (
				<OfficialScoreSheetPrint
					innerRef={contentRef}
					matches={matchup.matches}
					sheetSize={selectedPrintOptions.sheetSize === 'half' ? 'Half Sheets' : 'Full Sheets'}
					podsResponse={podsResponse}
					timeZoneIana={matchup.timezone.ianaTzIdentifier}
					teamOneTitle={matchup.teamOneTitle ?? 'TBD'}
					teamTwoTitle={matchup.teamTwoTitle ?? 'TBD'}
					shouldDisplayPlayerNames={shouldDisplayPlayerNames}
				/>
			)}

			{selectedPrintOptions.sheetType === 'mlp' && (
				<MlpScoreSheetPrint
					innerRef={contentRef}
					matches={matchup.matches}
					teamOneTitle={matchup.teamOneTitle ?? 'TBD'}
					teamTwoTitle={matchup.teamTwoTitle ?? 'TBD'}
					sheetSize={'Half Sheets'} // NOTE: MLP only has half sheets, bcs is the big sheet
					rotations={rotationsInfo?.data ?? { matchUuid: '', teamOne: [], teamTwo: [] }}
					shouldDisplayPlayerNames={shouldDisplayPlayerNames}
				/>
			)}
		</div>
	);
};
